// src/pages/home/components/Categories/Categories.jsx
import React, { useState, useEffect, useContext } from "react";
import { Container, Carousel, Row, Col } from "react-bootstrap";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import CategoryCard from "../../../../components/customer/cards/categoryCard/CategoryCard";
import { CategoriesSkeleton } from "../../../../components/general/skeleton/SkeletonBlock";
import MyContext from "../../../../context/MyContext";
import "../../../../assets/css/customer/home/categories/Categories.css";

const Categories = () => {
  const navigate = useNavigate();
  const { baseUrl, baseUrlForFiles } = useContext(MyContext);

  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const [itemsPerSlide, setItemsPerSlide] = useState(4);

  const [categoriesList, setCategoriesList] = useState([]);
  const [activeCategoryId, setActiveCategoryId] = useState(null);

  // ── viewAllCategories — categories + unki subcategories fetch karta hai
  //     (eager loaded, is liye alag se viewAllSubcategories call nahi karni padti) ──
  const viewAllCategories = () => {
    setLoading(true);
    axios
      .get(`${baseUrl}/customer/category/viewAllCategories`)
      .then((response) => {
        setLoading(false);
        if (
          response.data.hasOwnProperty("error") ||
          response.data.success === false
        ) {
          return;
        }
        const categories = response.data.categories || [];
        setCategoriesList(categories);
        if (categories.length > 0) {
          setActiveCategoryId(categories[0].id);
        }
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    viewAllCategories();
  }, []);

  // ── Active category ki subcategories nikalo ──
  const activeCategory = categoriesList.find((c) => c.id === activeCategoryId);
  const activeCategories = (activeCategory?.kmsubcategories || []).map((sub) => ({
    id: sub.id,
    name: sub.subcategoryname,
    parentCategory: activeCategory?.categoryname,
    parentCategoryId: activeCategory?.id,
    // Sirf backend ki asli image — koi default/fallback picture nahi.
    // Image na ho to CategoryCard sirf naam dikhata hai.
    image: sub.subcategoryimage ? `${baseUrlForFiles}/${sub.subcategoryimage}` : null,
  }));

  // ── Screen resize par itemsPerSlide update karne wala function (named) ──
  const updateItemsPerSlide = () => {
    const width = window.innerWidth;
    if (width < 500) setItemsPerSlide(1);
    else if (width < 768) setItemsPerSlide(2);
    else if (width < 992) setItemsPerSlide(3);
    else setItemsPerSlide(4);
  };

  useEffect(() => {
    updateItemsPerSlide();
    window.addEventListener("resize", updateItemsPerSlide);
    return () => window.removeEventListener("resize", updateItemsPerSlide);
  }, []);

  useEffect(() => {
    setActiveIndex(0);
  }, [activeCategoryId, itemsPerSlide]);

  const createSlides = () => {
    const slides = [];
    for (let i = 0; i < activeCategories.length; i += itemsPerSlide) {
      slides.push(activeCategories.slice(i, i + itemsPerSlide));
    }
    return slides;
  };

  // ── Category card click — subcategory data chahiye, curried function ──
  const goToProductsPage = (category) => () => {
    navigate("/products", {
      state: {
        category: category.parentCategory,
        subcategory: category.name,
      },
    });
  };

  const handleSelect = (idx) => setActiveIndex(idx);

  // ── Tab switch — category id chahiye, curried function ──
  const switchMainCategory = (categoryId) => () => {
    setActiveCategoryId(categoryId);
    setActiveIndex(0);
  };

  const slides = createSlides();

  if (loading) return <CategoriesSkeleton count={itemsPerSlide} />;

  return (
    <section className="categories-section">
      <Container>
        <div className="categories-header">
          <h2 className="categories-title">Shop By Category</h2>
          <p className="categories-subtitle">
            Explore our wide range of agricultural products
          </p>
          <div className="main-category-buttons">
            {categoriesList.map((cat) => (
              <button
                key={cat.id}
                className={`main-category-btn ${activeCategoryId === cat.id ? "active" : ""}`}
                onClick={switchMainCategory(cat.id)}
              >
                {cat.categoryname}
              </button>
            ))}
          </div>
        </div>

        {activeCategories.length === 0 ? (
          <p style={{ textAlign: "center", color: "#888" }}>
            No subcategories found.
          </p>
        ) : (
          <div className="categories-slider">
            <Carousel
              activeIndex={activeIndex}
              onSelect={handleSelect}
              indicators={true}
              interval={3500}
              pause="hover"
              prevIcon={
                <div className="custom-arrow">
                  <FaChevronLeft />
                </div>
              }
              nextIcon={
                <div className="custom-arrow">
                  <FaChevronRight />
                </div>
              }
            >
              {slides.map((group, groupIndex) => (
                <Carousel.Item key={groupIndex}>
                  <Row className="categories-row justify-content-center flex-nowrap g-3">
                    {group.map((category, index) => (
                      <Col
                        key={category.id}
                        className="d-flex justify-content-center"
                      >
                        <CategoryCard
                          category={{
                            id: category.id,
                            name: category.name,
                            defaultImage: category.image,
                            parentCategory: category.parentCategory,
                          }}
                          onClick={goToProductsPage(category)}
                          delay={index * 0.1}
                        />
                      </Col>
                    ))}
                  </Row>
                </Carousel.Item>
              ))}
            </Carousel>
          </div>
        )}
      </Container>
    </section>
  );
};

export default Categories;
